import { request } from "../../../services/api.ts"
import type { BaseApiResponse, BasePagination } from "../../../types/api.ts"
import type {
  PortalAlbum,
  PortalCalendarEvent,
  PortalCustomerProfile,
  PortalDocument,
  PortalInvoice,
  PortalPhoto,
  PortalProjectDetail,
  PortalProjectListItem,
  PortalRsvpResponse
} from "../types/portal.types.ts"

const PATHS = {
  projects: "/project-management/projects/",
  projectDetails: (id: string) => `/project-management/projects/${id}/details/`,
  invoices: "/project-management/invoices/",
  documents: "/project-management/project-attachments/",
  albums: "/project-management/albums/",
  attachments: "/project-management/attachments/",
  attachmentUpload: "/project-management/attachments/upload/",
  profile: "/crm/customers/me/",
  calendarEvents: "/calendars/calendars/portal/events/",
  calendarEvent: (id: string) => `/calendars/calendars/portal/events/${id}/`,
  calendarEventRsvp: (id: string) => `/calendars/calendars/portal/events/${id}/rsvp/`,
  calendarEventRescheduleRequest: (id: string) =>
    `/calendars/calendars/portal/events/${id}/reschedule-request/`,
  calendarEventCancel: (id: string) => `/calendars/calendars/portal/events/${id}/cancel/`,
  calendarEventAcceptProposedTime: (id: string) =>
    `/calendars/calendars/portal/events/${id}/accept-proposed-time/`,
  calendarEventReminderPreference: (id: string) =>
    `/calendars/calendars/portal/events/${id}/reminder-preference/`
}

// Every endpoint below already scopes its queryset to the authenticated
// customer's own company/projects on the server (ProjectViewSet, InvoiceViewSet,
// ProjectAttachmentViewSet.visible_documents_for, AlbumViewSet,
// AttachmentViewSet.visible_attachments_for) — the portal composes what
// already exists. See ezbuild_back's docs/kb/decisions/
// 2026-09-22-project-attachment-customer-visibility-gate.md for the one
// gate that was added, and this session's KB entry for the two-way upload
// wiring (uploaded_by OR-clause, no other backend change needed for photos).

export const getPortalProjects = () =>
  request.api.get<BasePagination<PortalProjectListItem>>(PATHS.projects)

export const getPortalProjectDetail = (projectId: string) =>
  request.api.get<PortalProjectDetail>(PATHS.projectDetails(projectId))

export const getPortalInvoices = (projectId: string) =>
  request.api.get<BasePagination<PortalInvoice>>(PATHS.invoices, {
    project: projectId,
    page_size: 50
  })

// GET /project-management/project-attachments/?lead=<id> — for a customer,
// returns rows the GC shared (is_visible_to_customer=True) plus the
// customer's own uploads (uploaded_by=self), never anything else.
export const getPortalDocuments = (projectId: string) =>
  request.api.get<BasePagination<PortalDocument>>(PATHS.documents, {
    lead: projectId,
    page_size: 100
  })

interface UploadDocumentPayload {
  projectId: string
  file: File
  note?: string
}

// POST /project-management/project-attachments/ — the customer's own
// "Upload and share" action. `uploaded_by` is stamped server-side from the
// authenticated user; nothing here can attribute the upload to anyone else.
export const uploadPortalDocument = ({ projectId, file, note }: UploadDocumentPayload) => {
  const formData = new FormData()
  formData.append("lead", projectId)
  formData.append("file_name", file.name)
  formData.append("file", file)
  if (note) formData.append("revision_note", note)
  return request.api.post<BaseApiResponse<PortalDocument>>(PATHS.documents, formData, {
    headers: { "Content-Type": "multipart/form-data" }
  })
}

// GET /project-management/albums/?project=<id> — only ever returns albums
// with is_visible_to_customer=True on the caller's own project.
export const getPortalAlbums = (projectId: string) =>
  request.api.get<BasePagination<PortalAlbum>>(PATHS.albums, {
    project: projectId,
    page_size: 50
  })

// GET /project-management/attachments/?project=<id> — for a customer,
// returns their own uploads (created_by=self) plus anything in an
// is_visible_to_customer album on their project.
export const getPortalPhotos = (projectId: string) =>
  request.api.get<PortalPhoto[]>(PATHS.attachments, { project: projectId })

interface UploadPhotoPayload {
  projectId: string
  file: File
}

// POST /project-management/attachments/upload/ — open to any authenticated
// role; model_type=PROJECT + object_id=<project id> is what every existing
// project-level photo upload already sends.
export const uploadPortalPhoto = ({ projectId, file }: UploadPhotoPayload) => {
  const formData = new FormData()
  formData.append("project_id", projectId)
  formData.append("object_id", projectId)
  formData.append("model_type", "project")
  formData.append("file", file)
  return request.api.post<BaseApiResponse<PortalPhoto>>(PATHS.attachmentUpload, formData, {
    headers: { "Content-Type": "multipart/form-data" }
  })
}

// GET /crm/customers/me/ — unwrapped, matching every other GET on this API.
export const getPortalProfile = () => request.api.get<PortalCustomerProfile>(PATHS.profile)

export interface UpdatePortalProfilePayload {
  first_name?: string
  last_name?: string
  phone?: string | null
  alternate_phone?: string | null
  address?: string | null
  address_2?: string | null
  city?: string | null
  state?: string | null
  zip_code?: string | null
  preferred_contact_method?: string | null
  expected_updated_at: string
}

// PATCH /crm/customers/me/ — envelope-wrapped like every other write on
// this API; a 409 means someone else's change landed first, read by the
// thunk from `response.status`, not the envelope.
export const updatePortalProfile = (data: UpdatePortalProfilePayload) =>
  request.api.patch<BaseApiResponse<PortalCustomerProfile>>(PATHS.profile, data)

// GET /calendars/calendars/portal/events/ — plain array (not paginated),
// server-scoped to events this customer was explicitly, individually
// invited to (PortalCalendarEventViewSet / visible_calendar_events_for). A
// project association alone never appears here.
export const getPortalCalendarEvents = (params?: {
  project?: string
  start?: string
  end?: string
}) => request.api.get<PortalCalendarEvent[]>(PATHS.calendarEvents, params)

export const getPortalCalendarEvent = (id: string) =>
  request.api.get<PortalCalendarEvent>(`${PATHS.calendarEvents}${id}/`)

// POST .../rsvp/ — applies only to the caller's own invitation; the
// server-side response never touches another attendee's status.
export const rsvpToPortalCalendarEvent = (id: string, response: PortalRsvpResponse) =>
  request.api.post<BaseApiResponse<PortalCalendarEvent>>(PATHS.calendarEventRsvp(id), {
    status: response
  })

export interface RequestCalendarRescheduleParams {
  eventId: string
  suggestedStartAt?: string
  suggestedEndAt?: string
  note?: string
}

// POST .../reschedule-request/ — never changes the appointment itself; the
// GC resolves it by actually moving the event's time on their own calendar.
export const requestPortalCalendarReschedule = ({
  eventId,
  suggestedStartAt,
  suggestedEndAt,
  note
}: RequestCalendarRescheduleParams) =>
  request.api.post<BaseApiResponse<PortalCalendarEvent>>(
    PATHS.calendarEventRescheduleRequest(eventId),
    {
      suggested_start_at: suggestedStartAt,
      suggested_end_at: suggestedEndAt,
      note
    }
  )

export interface CreatePortalCalendarEventPayload {
  kind: "appointment" | "reminder"
  title: string
  start_at: string
  end_at: string
  all_day?: boolean
  timezone?: string
  location?: string
  meeting_link?: string
  description?: string
  project_id?: string | null
  /** Project-team member ids to invite — validated server-side against the
   * project's own assigned roster. Required (non-empty) for a fresh
   * appointment; must be empty for a reminder. */
  participant_ids?: string[]
  external_emails?: string[]
  /** The submitter's own reminder preference — see `PortalCalendarReminder`. */
  reminder_offset_minutes?: number | null
  remind_at_start?: boolean
}

// POST /calendars/calendars/portal/events/ — a real `CalendarEvent`, not a
// portal-only copy. "appointment" gets a GC attendee the server resolves
// from the project's own assignment (never client-supplied); "reminder"
// gets no attendee at all, so it never reaches the GC's calendar.
export const createPortalCalendarEvent = (payload: CreatePortalCalendarEventPayload) =>
  request.api.post<BaseApiResponse<PortalCalendarEvent>>(PATHS.calendarEvents, payload)

export type UpdatePortalCalendarEventPayload = Partial<
  Omit<CreatePortalCalendarEventPayload, "kind" | "project_id">
>

// PATCH — only the customer's own event (`can_edit`); the server 403s any
// attempt against a GC-created appointment.
export const updatePortalCalendarEvent = (id: string, payload: UpdatePortalCalendarEventPayload) =>
  request.api.patch<BaseApiResponse<PortalCalendarEvent>>(PATHS.calendarEvent(id), payload)

export const cancelPortalCalendarEvent = (id: string) =>
  request.api.post<BaseApiResponse<PortalCalendarEvent>>(PATHS.calendarEventCancel(id))

// The customer accepting a GC-proposed time — moves the same event, never
// creates a second one.
export const acceptProposedCalendarTime = (id: string) =>
  request.api.post<BaseApiResponse<PortalCalendarEvent>>(PATHS.calendarEventAcceptProposedTime(id))

export interface UpdateReminderPreferencePayload {
  reminder_offset_minutes: number | null
  remind_at_start: boolean
}

// POST .../reminder-preference/ — always the caller's own row, whichever
// event this is (their own appointment, or one a GC invited them to);
// never anyone else's, and never routed through the main edit form's PATCH.
export const updatePortalReminderPreference = (id: string, payload: UpdateReminderPreferencePayload) =>
  request.api.post<BaseApiResponse<PortalCalendarEvent>>(
    PATHS.calendarEventReminderPreference(id),
    payload
  )
